import React from "react";
import { useTenant } from "@/hooks/useTenant";
import { useCustomerCohortCounts } from "@/hooks/useCustomerCohortCounts";
import { useTenantConfig } from "@/hooks/useTenantConfig";
import { usePageViews } from "@/hooks/usePageViews";
import { useWeeklyConversationsEngaged } from "@/hooks/useWeeklyConversationsEngaged";
import {
  Globe,
  HandCoins,
  MessageCircleReply,
  Send,
  Users,
  UserSearch,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePreserveSearchParams } from "@/hooks/usePreserveSearchParams";
import { DashTile } from "./DashTile";
import { useWeeklyRevenue } from "@/hooks/useWeeklyRevenue";

interface KPITileProps {
  title: string;
  value: string | number;
  change: {
    value: number;
    isPositive: boolean;
    label: string;
  };
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  tooltip: string;
  onClick?: () => void;
}

export const KPITiles: React.FC = () => {
  const { currentTenant } = useTenant();
  // Die vier Kohorten-Zahlen kommen jetzt fertig aus der Datenbank. Vorher
  // wurde dafuer der komplette Kundenstamm geladen und im Browser gezaehlt --
  // bei M&M 7.228 Datensaetze in acht Anfragen, fuer vier Zahlen.
  const {
    thisWeekCustomers,
    lastWeekCustomers,
    thisWeekLeads,
    lastWeekLeads,
  } = useCustomerCohortCounts(currentTenant?.id || null);
  const { hasCalendar } = useTenantConfig();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { createPath } = usePreserveSearchParams();

  // Navigate to the customers list pre-filtered to leads ("Interessenten").
  const goToLeads = () => {
    const base = createPath("/customers");
    navigate(`${base}${base.includes("?") ? "&" : "?"}view=leads`);
  };
  const { data: pageViewsData, isLoading: pageViewsLoading } = usePageViews(currentTenant?.id || null);
  const { data: revenueData, isLoading: revenueLoading } = useWeeklyRevenue(currentTenant?.id || null);
  const {
    leadsMessaged,
    leadsMessagedChange,
    engagedCount,
    engagedChange,
    isLoading: engagedLoading,
  } = useWeeklyConversationsEngaged(currentTenant?.id || null);

  // Calculate customers vs leads using pre-computed status and cohort fields.
  // (Revenue is fetched separately via useWeeklyRevenue — a date-windowed query —
  // because the client appointments array is capped/oldest-first and unreliable.)
  const totalCustomers = { change: thisWeekCustomers - lastWeekCustomers };
  const totalLeads = { change: thisWeekLeads - lastWeekLeads };

  // Weekly revenue from the dedicated, date-windowed query.
  const numLocale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const twRevenue = revenueData?.thisWeekRevenue ?? 0;
  const lwRevenue = revenueData?.lastWeekRevenue ?? 0;
  const revenueDiff = twRevenue - lwRevenue;

  const kpiData: KPITileProps[] = [
    {
      title: t("weeklyWebsiteVisits"),
      value: pageViewsLoading ? "..." : (pageViewsData?.totalPageViews?.toLocaleString(i18n.language === 'de' ? 'de-DE' : 'en-US') || "0"),
      change: {
        value: pageViewsData?.weeklyChange || 0,
        isPositive: pageViewsData?.isPositive || false,
        label: pageViewsData?.changeLabel || t("vsLastWeek"),
      },
      icon: Globe,
      color: "text-primary",
      bgColor: "bg-primary/10",
      tooltip: t("weeklyWebsiteVisitsTooltip"),
    },
    {
      title: t("weeklyLeads"),
      value: thisWeekLeads.toLocaleString(i18n.language === 'de' ? 'de-DE' : 'en-US'),
      change: {
        value: totalLeads.change,
        isPositive: totalLeads.change >= 0,
        label: `${totalLeads.change >= 0 ? "+" : ""
          }${totalLeads.change} ${t("vsLastWeek")}`,
      },
      icon: UserSearch,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      tooltip: t("weeklyLeadsTooltip"),
      onClick: goToLeads,
    },
    // Only show customer and revenue tiles when calendar is enabled
    ...(hasCalendar ? [
      {
        title: t("weeklyCustomers"),
        value: thisWeekCustomers.toLocaleString(i18n.language === 'de' ? 'de-DE' : 'en-US'),
        change: {
          value: totalCustomers.change,
          isPositive: totalCustomers.change >= 0,
          label: `${totalCustomers.change >= 0 ? "+" : ""
            }${totalCustomers.change} ${t("vsLastWeek")}`,
        },
        icon: Users,
        color: "text-green-600",
        bgColor: "bg-green-50",
        tooltip: t("weeklyCustomersTooltip"),
      },
      {
        title: t("weeklyRevenue"),
        value: revenueLoading ? "..." : `${twRevenue.toLocaleString(numLocale)}€`,
        change: {
          value: revenueDiff,
          isPositive: revenueDiff >= 0,
          label: `${revenueDiff >= 0 ? "+" : ""}${revenueDiff.toLocaleString(numLocale)}€ ${t("vsLastWeek")}`,
        },
        icon: HandCoins,
        color: "text-secondary-foreground",
        bgColor: "bg-secondary",
        tooltip: t("weeklyRevenueTooltip"),
      },
    ] : []),
    // Only show Sylvia messaging metrics when calendar is disabled
    ...(!hasCalendar ? [
      {
        title: t("weeklyLeadsMessaged"),
        value: engagedLoading ? "..." : leadsMessaged.toLocaleString(i18n.language === 'de' ? 'de-DE' : 'en-US'),
        change: {
          value: leadsMessagedChange,
          isPositive: leadsMessagedChange >= 0,
          label: `${leadsMessagedChange >= 0 ? "+" : ""}${leadsMessagedChange} ${t("vsLastWeek")}`,
        },
        icon: Send,
        color: "text-blue-600",
        bgColor: "bg-blue-50",
        tooltip: t("weeklyLeadsMessagedTooltip"),
      },
      {
        title: t("weeklyConversationsEngaged"),
        value: engagedLoading ? "..." : engagedCount.toLocaleString(i18n.language === 'de' ? 'de-DE' : 'en-US'),
        change: {
          value: engagedChange,
          isPositive: engagedChange >= 0,
          label: `${engagedChange >= 0 ? "+" : ""}${engagedChange} ${t("vsLastWeek")}`,
        },
        icon: MessageCircleReply,
        color: "text-green-600",
        bgColor: "bg-green-50",
        tooltip: t("weeklyConversationsEngagedTooltip"),
      },
    ] : []),
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {kpiData.map((kpi) => (
        <DashTile
          key={kpi.title}
          title={kpi.title}
          value={String(kpi.value)}
          sub={kpi.change.label}
          tooltip={kpi.tooltip}
          icon={kpi.icon}
          color={kpi.color}
          bgColor={kpi.bgColor}
          onClick={kpi.onClick}
        />
      ))}
    </div>
  );
};

